import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { customerToday, formatHolidayWindow, nextHolidayWindow } from "@ckgo2/shared";
import type { Holiday } from "@ckgo2/shared/types";
import { Plus, Pencil, Trash2, Loader2, CalendarDays } from "lucide-react";

export default function HolidaysPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["holidays"],
    queryFn: () => api.get<ApiResponse<Holiday[]>>("/api/admin/holidays"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/holidays/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["holidays"] }),
  });

  const statusLabels: Record<string, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
  };

  const holidays = data?.data || [];
  // Pražský den, ne UTC: „nejbližší okno" tu počítá TÁŽ sdílená funkce, jakou nad týmiž
  // řádky volá panel filtrů na webu. S `toISOString()` by si administrace a zákazník
  // mezi 00:00 a 02:00 pražského času odpověděly jinak — operátor by viděl jako aktuální
  // okno, které zákazníkovi z webu už zmizelo.
  const today = customerToday();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.holidays.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("content.holidays.list.subtitle")}
          </p>
        </div>
        <Link to="/holidays/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("content.holidays.list.addHoliday")}
        </Link>
      </div>

      {holidays.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center dark:border-gray-700">
          <CalendarDays className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400">{t("content.holidays.list.empty")}</p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-200 dark:divide-gray-700">
          {holidays.map((holiday) => {
            // Nejbližší budoucí okno je jediný údaj, který návštěvník uvidí na tlačítku —
            // proto stojí i tady. Svátek, jehož všechna okna proběhla, na to musí upozornit:
            // tlačítko na webu pak nemá co předvyplnit.
            const upcoming = nextHolidayWindow(holiday.windows || [], today);
            return (
              <div key={holiday.id} className="flex items-center justify-between py-3">
                <div className="min-w-0 flex-1 pr-4">
                  <p className="truncate font-medium text-gray-900 dark:text-white">
                    {holiday.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("content.holidays.list.meta", {
                      window: upcoming
                        ? formatHolidayWindow(upcoming)
                        : t("content.holidays.list.noUpcoming"),
                      windows: (holiday.windows || []).length,
                      order: holiday.sortOrder,
                      status: statusLabels[holiday.status] || holiday.status,
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/holidays/${holiday.id}`}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                  >
                    <Pencil className="h-4 w-4" />
                  </Link>
                  <button
                    title={t("common.actions.delete")}
                    onClick={() => {
                      if (confirm(t("content.holidays.list.confirmDelete")))
                        deleteMutation.mutate(holiday.id);
                    }}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
